import { PlayerManagerContext } from '../../shared/services/player-manager-context.service';
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { Subscription } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { Player, PublicPlayerProfile } from "../../shared/models/player";
import { PlayerService } from "../../shared/services/player.service";
import { ChatService } from "../../shared/services/chat.service";
import { navigateBackOrFallback } from "../../shared/screen-shell/screen-navigation.utils";

@Component({
  selector: "app-public-player-profile",
  templateUrl: "./public-player-profile.component.html",
  styleUrls: ["../player.component.css"],
  standalone: false
})
export class PublicPlayerProfileComponent implements OnDestroy, OnInit {

  public playerId: string;
  public managerContext: PlayerManagerContext | null = null;
  public profile: PublicPlayerProfile;
  public loading = true;
  public loadingVisible = false;
  public unavailable = false;
  public errorMsg: string;
  public chatLoading = false;
  public moderationLoading = false;
  public showBlockDialog = false;
  public showReportDialog = false;
  public showAvatarPreview = false;
  public isOwnProfile(): boolean { return !!this.currentPlayer?.id && this.currentPlayer.id === this.profile?.id; }
  public openAvatar(): void {
    if (this.isOwnProfile()) void this.router.navigate(['/player-detail', false], { queryParams: { section: 'avatar' } });
    else if (this.profile?.avatar) this.showAvatarPreview = true;
  }
  public editOwnName(): void {
    if (this.isOwnProfile()) void this.router.navigate(['/player-detail', false], { queryParams: { section: 'profile' } });
  }
  public reportReason = "HARASSMENT";
  public reportDetails = "";
  private contextEventId: string;
  private currentPlayer: Player;
  private loadingTimer: number;
  private profileSubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private playerService: PlayerService,
    private translate: TranslateService,
    private chatService: ChatService,
    private router: Router,
    private location: Location
  ) {}

  public ngOnInit(): void {
    this.currentPlayer = this.playerService.peekCurrentPlayer();
    this.profileSubscription = this.route.data.subscribe(data => {
      this.playerId = this.route.snapshot.params["playerId"];
      this.contextEventId = this.route.snapshot.queryParamMap.get("contextEventId");
      const prepared = data['screen'];
      this.managerContext = prepared?.managerContext || null;
      if (prepared) { this.profile = prepared.profile; this.unavailable = prepared.unavailable; this.hideLoading(); }
      else this.loadProfile();
    });
  }

  public ngOnDestroy(): void {
    this.profileSubscription?.unsubscribe();
    this.hideLoading();
  }

  public displayCity(): string {
    if (!this.profile || !this.profile.city) {
      return "";
    }
    return this.profile.city.split(",")[0].trim();
  }

  public joinedAt(): Date {
    if (this.profile && this.profile.joinedAt) {
      return new Date(this.profile.joinedAt);
    }
    if (!this.profile || !this.profile.id || !/^[0-9a-fA-F]{24}$/.test(this.profile.id)) {
      return null;
    }
    return new Date(parseInt(this.profile.id.substring(0, 8), 16) * 1000);
  }

  public profileStatValue(value: number): number {
    return typeof value === "number" && !isNaN(value) ? value : 0;
  }

  public teamGoalScore(): string {
    const score = this.profileStatValue(this.profile && this.profile.stats ? this.profile.stats.teamGoalDifference : null);
    return score > 0 ? "+" + score : String(score);
  }

  public hasContact(): boolean {
    return !!(this.profile?.contactActions?.canCall || this.profile?.contactActions?.canEmail);
  }

  public hasProfileActions(): boolean {
    return !!(this.canChat() || this.hasContact() || this.canModerate());
  }

  public canModerate(): boolean {
    return !!(this.profile && this.currentPlayer && this.currentPlayer.id && this.currentPlayer.id !== this.profile.id);
  }

  public blockPlayer(): void {
    if (!this.canModerate() || this.moderationLoading) return;
    this.errorMsg = null;
    this.moderationLoading = true;
    this.playerService.blockPlayer(this.profile.id).subscribe(
      () => this.finishModeration(),
      () => { this.moderationLoading = false; this.errorMsg = this.translate.instant("safety.actionFailed"); }
    );
  }

  public reportPlayer(): void {
    if (!this.canModerate() || this.moderationLoading) return;
    this.errorMsg = null;
    this.moderationLoading = true;
    this.playerService.reportPlayer(this.profile.id, this.reportReason, this.reportDetails).subscribe(
      () => this.finishModeration(),
      () => { this.moderationLoading = false; this.errorMsg = this.translate.instant("safety.actionFailed"); }
    );
  }

  private finishModeration(): void {
    this.moderationLoading = false;
    this.showBlockDialog = false;
    this.showReportDialog = false;
    this.router.navigate(["/event-list"]);
  }

  public isAuthenticatedProfileView(): boolean {
    return !!(this.currentPlayer && this.currentPlayer.id);
  }

  public headerTitle(): string {
    if (this.profile) {
      return this.profile.nickName || this.profile.name || this.translate.instant("common.details");
    }
    return this.translate.instant("common.details");
  }

  public goBack(): void {
    navigateBackOrFallback(this.location, this.router, ["/event-list"]);
  }

  public canChat(): boolean {
    return this.profile?.contactActions?.canChat === true;
  }

  public openChat(): void {
    if (!this.canChat() || this.chatLoading) {
      return;
    }
    this.chatLoading = true;
    this.chatService.openDirectChat(this.profile.id).subscribe(
      (thread) => {
        this.chatLoading = false;
        this.router.navigate(["/messages", { chatId: thread.chat.id }]);
      },
      (response) => {
        console.log(response && response.error ? response.error : response);
        this.chatLoading = false;
      }
    );
  }

  public shareProfile(): void {
    const shareUrl = window.location.href;
    const title = this.profile && this.profile.nickName ? this.profile.nickName : this.translate.instant("players.shareProfileFallbackTitle");
    const shareData = {
      title,
      text: shareUrl,
      url: shareUrl
    };

    const navigatorWithShare = navigator as Navigator & {
      share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
      clipboard?: Clipboard;
    };

    if (navigatorWithShare.share) {
      navigatorWithShare.share(shareData).catch(() => {});
      return;
    }

    if (navigatorWithShare.clipboard && navigatorWithShare.clipboard.writeText) {
      navigatorWithShare.clipboard.writeText(shareUrl).catch(() => this.copyTextFallback(shareUrl));
      return;
    }

    this.copyTextFallback(shareUrl);
  }

  private loadProfile(): void {
    this.showLoading();
    this.unavailable = false;
    this.errorMsg = null;
    this.playerService.getPublicProfile(this.playerId, this.contextEventId,
      new URLSearchParams(this.route.snapshot.fragment || '').get('share')).subscribe(
      (profile) => {
        this.profile = profile;
        this.hideLoading();
      },
      () => {
        this.hideLoading();
        this.unavailable = true;
      }
    );
  }

  private showLoading(): void {
    this.loading = true;
    this.loadingVisible = false;
    this.clearLoadingTimer();
    this.loadingTimer = window.setTimeout(() => {
      this.loadingVisible = this.loading;
      this.loadingTimer = null;
    }, 300);
  }

  private hideLoading(): void {
    this.loading = false;
    this.loadingVisible = false;
    this.clearLoadingTimer();
  }

  private clearLoadingTimer(): void {
    if (!this.loadingTimer) {
      return;
    }
    window.clearTimeout(this.loadingTimer);
    this.loadingTimer = null;
  }

  private copyTextFallback(value: string): void {
    const textArea = document.createElement("textarea");
    textArea.value = value;
    textArea.setAttribute("readonly", "true");
    textArea.style.position = "absolute";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  }

}
